import { makeStyles, Typography, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useQuery } from "@tanstack/react-query";
import GenericButton from "components/Buttons/GenericButton";
import { getFilteredWorkcentersSnapshot } from "crud/rent.crud";
import { useModal } from "hooks/useModal";
import { useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import StepContainer from "views/admin/RENT/Create/Steps/step-container";
import WorkCenterAddModal from "./Modal/work-center-add-modal";
import WorkCenterDetailModal from "./Modal/work-center-detail-modal";
import WorkCentersTable from "./work-centers-table";
import WorkCentersFilterTable from "./work-centers-table-filter";

const useStyles = makeStyles((theme) => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

export default function WorkCentersInfoTab() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { id } = useParams();

  const defaultFormValues = {
    name: "",
    provinceId: "",
    municipalityId: "",
    secundaryActivityId: "",
    pageIndex: 1,
    pageSize: 15,
  };

  const form = useForm({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  const classes = useStyles();
  const { isOpen, modalData, openModal, closeModal } = useModal();
  const { isOpen: addModalOpened, openModal: openAddModal, closeModal: closeAddModal } = useModal();

  return (
    <>
      <StepContainer
        title="Dados de Centros"
        action={
          <GenericButton
            color="tertiary"
            size={isSmDown ? "medium" : "small"}
            fullWidth={isSmDown}
            onClick={openAddModal}
          >
            Adicionar Centro
          </GenericButton>
        }
      >
        <FormProvider {...form}>
          <WorkCentersInfoTabInner
            id={id}
            defaultFormValues={defaultFormValues}
            openModal={openModal}
          />
        </FormProvider>
      </StepContainer>

      <WorkCenterDetailModal open={isOpen} data={modalData} onClose={closeModal} />

      {addModalOpened && <WorkCenterAddModal open={addModalOpened} onClose={closeAddModal} />}
    </>
  );
}

function WorkCentersInfoTabInner({ id, defaultFormValues, openModal }) {
  const classes = useStyles();
  const { getValues, setValue, watch, reset } = useFormContext();
  const submittedFilterRef = useRef(null);

  const pageIndex = watch("pageIndex") ?? 1;
  const pageSize = watch("pageSize") ?? 15;

  const buildPayload = (values, override) => {
    const effectivePageIndex = override?.pageIndex ?? values.pageIndex ?? 1;
    const effectivePageSize = override?.pageSize ?? values.pageSize ?? 15;

    return {
      id,
      name: values.name ?? "",
      provinceId: values.provinceId ?? "",
      municipalityId: values.municipalityId ?? "",
      secundaryActivityId: values.secundaryActivityId ?? "",
      pageIndex: effectivePageIndex,
      pageSize: effectivePageSize,
    };
  };

  const [queryParams, setQueryParams] = useState(() => buildPayload(getValues()));

  const workCentersQuery = useQuery({
    queryKey: ["rent", "workcentersSnapshot", id, queryParams],
    queryFn: () => getFilteredWorkcentersSnapshot(queryParams),
    enabled: !!id && !!queryParams,
    keepPreviousData: true,
    select: (response) => {
      if (!response) return { data: [], totalCount: 0, totalPages: 0 };

      return {
        data: response.data,
        totalCount: response.totalCount,
        totalPages: response.totalPages,
      };
    },
  });

  return (
    <div className={classes.form}>
      <WorkCentersFilterTable
        onSubmit={() => {
          const values = getValues();

          const next = buildPayload(values, {
            pageIndex: 1,
            pageSize: values.pageSize ?? defaultFormValues.pageSize ?? 15,
          });

          submittedFilterRef.current = next;
          setQueryParams(next);

          if ((values.pageIndex ?? 1) !== 1) {
            setValue("pageIndex", 1);
          }
        }}
        onReset={() => {
          reset(defaultFormValues);

          const next = buildPayload(defaultFormValues, {
            pageIndex: 1,
            pageSize: defaultFormValues.pageSize,
          });
          submittedFilterRef.current = next;
          setQueryParams(next);
        }}
      />

      <Typography
        style={{
          fontWeight: 600,
        }}
        component="p"
        variant="body2"
      >
        Na tabela em baixo estão listados todos os centros de trabalho que foram submetidos na criação do relatório
        RENT. Poderá editar as informações entrando no detalhe de cada centro de trabalho. Após gravar ou adicionar o
        centro de trabalho, a lista de centros de trabalho no menu utilizadores será actualizada.
      </Typography>

      <WorkCentersTable
        handleOpenDetails={openModal}
        data={workCentersQuery.data}
        isFetching={workCentersQuery.isFetching}
        pageIndex={pageIndex}
        onPageChange={(_, value) => {
          setValue("pageIndex", value);

          const base = submittedFilterRef.current ?? buildPayload(getValues());
          const next = {
            ...base,
            pageIndex: value,
            pageSize,
          };
          submittedFilterRef.current = next;
          setQueryParams(next);
        }}
      />
    </div>
  );
}
